import { INLINES } from '@contentful/rich-text-types';
import { buttonLink } from '@insify/css/classes.module.css';
import { ChevronRight } from '@insify/design-system/icons';
import { textSmSemibold } from '@insify/design-system/typography';
import { SearchPage, WizardLayoutFormWithCommonSection, useWizard } from '@insify/funnel-components';
import { TrackedButton } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCommands } from '../../hooks/useCommands';
import { useParamsQuery } from '../../hooks/useQueries';
import { stepsContext } from '../../hooks/useSteps';

import styles from './Company.module.css';
import { NoResult } from './NoResult';
import { SearchItem } from './SearchItem';
import { useCompanyContent } from './useCompanyContent';
import { mapCompanyToId, useCompanySearch } from './useCompanySearch';

import type { RegisteredCompany } from '../../models/company';

export const CompanyPage = () => {
  const { t } = useTranslation(['funnel', 'common']);
  const { isStale, data: { companyRegistrationStatus, requestCompanyDetails, company: selectedCompany = null } = {} } =
    useParamsQuery();
  const [searchString, setSearchString] = useState('');
  const { sendCommandAndWaitForInvalidations, isLoading } = useCommands();
  const { showNextPage, showPreviousPage } = useWizard();
  const { richContent, translations } = useCompanyContent();
  const { showMore, results } = useCompanySearch(searchString);
  const [triggerShowingNextPage, setTriggerShowingNextPage] = useState(false);
  const { isEnteringCompanyDetails, setIsEnteringCompanyDetails } = stepsContext.useHook();

  const selectCompany = async (company: RegisteredCompany) => {
    if (!selectedCompany || selectedCompany.id !== company.id) {
      setIsEnteringCompanyDetails(false);
      await sendCommandAndWaitForInvalidations({
        type: 'selectCompany',
        payload: { id: company.id.value, keepActivitiesAndClaims: true },
      });
    }
    setTriggerShowingNextPage(true);
  };

  const enterCompanyDetailsManually = () => {
    setIsEnteringCompanyDetails(true);
    setTriggerShowingNextPage(true);
  };

  const selectUnregisteredCompany = async () => {
    setIsEnteringCompanyDetails(false);
    await sendCommandAndWaitForInvalidations({
      type: 'confirmCompanyNotYetRegistered',
      payload: { keepActivitiesAndClaims: true },
    });
    setTriggerShowingNextPage(true);
  };

  const hasSelectedIncompleteCompany = requestCompanyDetails && !isEnteringCompanyDetails;
  const hasCompanyRegistrationStatus = !!companyRegistrationStatus;
  const canProceed = isEnteringCompanyDetails || hasSelectedIncompleteCompany || hasCompanyRegistrationStatus;

  useEffect(() => {
    if (triggerShowingNextPage && !isStale && canProceed) {
      showNextPage();
      setTriggerShowingNextPage(false);
    }
  }, [triggerShowingNextPage, isStale, isEnteringCompanyDetails, companyRegistrationStatus, requestCompanyDetails]);

  const actions = [
    <li key='enter-details-manually-button'>
      <TrackedButton
        data-testid='enter-details-manually-button'
        className={clsx(textSmSemibold, buttonLink, styles.action)}
        type='button'
        objectiv={{ id: 'enterDetailsManually' }}
        onClick={enterCompanyDetailsManually}
        disabled={isLoading}
      >
        {t('funnel:company_button_enter_manually')}
        <ChevronRight className={styles.icon} />
      </TrackedButton>
    </li>,
  ];

  return (
    <WizardLayoutFormWithCommonSection
      backCopy={t('common:back')}
      onBack={showPreviousPage}
      isForwardDisabled={true}
      forwardCopy={t('common:continue')}
      isSubmitting={false}
      onSubmit={() => null}
    >
      <SearchPage
        richContent={{
          content: richContent,
          renderNode: {
            [INLINES.HYPERLINK]: (node, children): JSX.Element => (
              <a href={node.data?.uri} target='_blank' rel='noreferrer'>
                {children}
              </a>
            ),
          },
        }}
        translations={translations}
        isSubmitting={isLoading}
        searchResultListClassName={styles.searchResultList}
        Item={SearchItem}
        searchActions={actions}
        noResult={<NoResult />}
        options={results}
        selected={selectedCompany}
        mapOptionToId={mapCompanyToId}
        eventHandlers={{
          onSearch: setSearchString,
          onSelect: selectCompany,
          onContinue: showNextPage,
          onContinueWithoutValue: selectUnregisteredCompany,
          onShowMore: showMore,
        }}
      />
    </WizardLayoutFormWithCommonSection>
  );
};
